import { useQueryClient } from "@tanstack/react-query";
import { LayoutGroup, motion } from "motion/react";
import { Plus } from "lucide-react";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { prefetchQuickAdd, usePrefetchTab } from "@/lib/queries";
import { press, spring } from "@/lib/motion";
import { UpdatePrompt } from "@/pwa/UpdatePrompt";
import { useOfflineSync } from "@/pwa/useOfflineSync";
import { MORE_ITEMS, TAB_ITEMS } from "./nav";

// Quick add's sheet (vaul + the form) is its own chunk so it stays off Inicio's first load (#67);
// it's fetched a moment after the app shows, or on press-in of (+), so it opens without a wait
const loadAddSheet = () => import("@/features/transactions/AddTransactionSheet");
const AddTransactionSheet = lazy(() =>
  loadAddSheet().then((m) => ({ default: m.AddTransactionSheet })),
);

export function AppShell({ children }: { children: ReactNode }) {
  const [adding, setAdding] = useState(false);
  // mounted on the first open and kept, so closing still animates
  const [sheetMounted, setSheetMounted] = useState(false);
  useOfflineSync();
  const openAdd = () => {
    setSheetMounted(true);
    setAdding(true);
  };
  const qc = useQueryClient();
  useEffect(() => {
    const id = setTimeout(() => {
      void loadAddSheet();
      prefetchQuickAdd(qc);
    }, 2000);
    return () => clearTimeout(id);
  }, [qc]);
  return (
    <div className="min-h-dvh">
      <div className="ambient" aria-hidden />
      <Sidebar onAdd={openAdd} />
      <main className="lg:pl-[248px]">{children}</main>
      <TabBar onAdd={openAdd} />
      <UpdatePrompt />
      {sheetMounted && (
        <Suspense fallback={null}>
          <AddTransactionSheet open={adding} onOpenChange={setAdding} />
        </Suspense>
      )}
    </div>
  );
}

function isActive(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  if (to === "/mas")
    return MORE_ITEMS.some((i) => pathname.startsWith(i.to)) || pathname === "/mas";
  return pathname.startsWith(to);
}

function TabBar({ onAdd }: { onAdd: () => void }) {
  const { pathname } = useLocation();
  const prefetch = usePrefetchTab();
  const [left, right] = [TAB_ITEMS.slice(0, 2), TAB_ITEMS.slice(2)];
  const renderTab = (item: (typeof TAB_ITEMS)[number]) => {
    const active = isActive(pathname, item.to);
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onPointerDown={() => prefetch(item.to)}
        className={cn(
          "relative flex flex-1 flex-col items-center gap-0.5 rounded-full py-1.5 text-[10.5px] font-medium transition-colors",
          active ? "text-accent" : "text-muted",
        )}
      >
        {active && (
          <motion.span
            layoutId="tab-pill"
            transition={spring.snappy}
            className="absolute inset-0 rounded-full bg-accent-soft"
          />
        )}
        <item.icon className="relative size-[22px]" strokeWidth={active ? 2.2 : 1.8} aria-hidden />
        <span className="relative">{item.label}</span>
      </NavLink>
    );
  };
  return (
    <nav
      aria-label="Principal"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-4 safe-bottom lg:hidden"
    >
      <LayoutGroup>
        <div className="pointer-events-auto mx-auto flex max-w-md items-center gap-1 rounded-full p-1.5 glass">
          {left.map(renderTab)}
          <motion.button
            type="button"
            whileTap={press}
            onPointerDown={() => void loadAddSheet()}
            onClick={onAdd}
            aria-label="Agregar gasto"
            className="mx-1 grid size-12 shrink-0 place-items-center rounded-full bg-accent text-accent-contrast shadow-[0_8px_24px_-6px_var(--accent)]"
          >
            <Plus className="size-6" strokeWidth={2.4} />
          </motion.button>
          {right.map(renderTab)}
        </div>
      </LayoutGroup>
    </nav>
  );
}

function Sidebar({ onAdd }: { onAdd: () => void }) {
  const { pathname } = useLocation();
  const prefetch = usePrefetchTab();
  const items = [...TAB_ITEMS.filter((t) => t.to !== "/mas"), ...MORE_ITEMS];
  return (
    <aside className="fixed inset-y-3 left-3 z-30 hidden w-[224px] flex-col rounded-[24px] p-3 glass lg:flex">
      <div className="flex items-center gap-2.5 px-2 py-3">
        <img src="/icon.svg" alt="" className="size-8 rounded-[9px]" />
        <span className="text-headline">SavingApp</span>
      </div>
      <motion.button
        type="button"
        whileTap={press}
        onPointerEnter={() => void loadAddSheet()}
        onClick={onAdd}
        className="mt-1 mb-3 flex items-center justify-center gap-2 rounded-[12px] bg-accent py-2.5 text-subhead font-semibold text-accent-contrast"
      >
        <Plus className="size-4" strokeWidth={2.6} /> Agregar gasto
      </motion.button>
      <nav aria-label="Principal" className="no-scrollbar flex-1 space-y-0.5 overflow-y-auto">
        <LayoutGroup>
          {items.map((item) => {
            const active = isActive(pathname, item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onPointerEnter={() => prefetch(item.to)}
                className={cn(
                  "relative flex items-center gap-3 rounded-[10px] px-3 py-2 text-subhead transition-colors",
                  active ? "font-semibold text-accent" : "text-text/80 hover:bg-surface-2",
                )}
              >
                {active && (
                  <motion.span
                    layoutId="side-pill"
                    transition={spring.snappy}
                    className="absolute inset-0 rounded-[10px] bg-accent-soft"
                  />
                )}
                <item.icon className="relative size-[18px]" aria-hidden />
                <span className="relative truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </LayoutGroup>
      </nav>
    </aside>
  );
}
